/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchContacts, createContact, updateContact, deleteContact } from './contactsApi';
import { ContactsState, Contact } from './types';

const initialState: ContactsState = {
    error: null,
    isFetching: false,
    isMutating: false,
    contacts: [],
};

export const contactsSlice = createSlice({
    name: 'contacts',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchContacts.pending, (state: ContactsState) => {
            state.isFetching = true;
            state.error = null;
        });

        builder.addCase(fetchContacts.fulfilled, (state: ContactsState, action: PayloadAction<Contact[]>) => {
            state.isFetching = false;
            state.contacts = action.payload;
        });

        builder.addCase(fetchContacts.rejected, (state: ContactsState, action: ReturnType<typeof fetchContacts.rejected>) => {
            state.isFetching = false;
            state.error = action.payload ?? action.error?.message ?? 'Unknown error';
            state.contacts = [];
        });

        builder.addCase(createContact.pending, (state: ContactsState) => {
            state.isMutating = true;
        });
        builder.addCase(createContact.fulfilled, (state: ContactsState, action: PayloadAction<Contact>) => {
            state.isMutating = false;
            state.contacts = [...state.contacts, action.payload].sort((a, b) => {
                if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                return a.name.localeCompare(b.name);
            });
        });
        builder.addCase(createContact.rejected, (state: ContactsState) => {
            state.isMutating = false;
        });

        builder.addCase(updateContact.pending, (state: ContactsState) => {
            state.isMutating = true;
        });
        builder.addCase(updateContact.fulfilled, (state: ContactsState, action: PayloadAction<Contact>) => {
            state.isMutating = false;
            state.contacts = state.contacts
                .map((contact) => (contact.id === action.payload.id ? action.payload : contact))
                .sort((a, b) => {
                    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
                    return a.name.localeCompare(b.name);
                });
        });
        builder.addCase(updateContact.rejected, (state: ContactsState) => {
            state.isMutating = false;
        });

        builder.addCase(deleteContact.pending, (state: ContactsState) => {
            state.isMutating = true;
        });
        builder.addCase(deleteContact.fulfilled, (state: ContactsState, action: PayloadAction<number>) => {
            state.isMutating = false;
            state.contacts = state.contacts.filter((contact) => contact.id !== action.payload);
        });
        builder.addCase(deleteContact.rejected, (state: ContactsState) => {
            state.isMutating = false;
        });
    },
});

export default contactsSlice.reducer;
